import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarPlus, Calculator, Star, MapPin } from "lucide-react";
import { toast } from "sonner";
import { today } from "@/lib/constants";

export default function Reserver() {
  const [companies, setCompanies] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [form, setForm] = useState({ company_id: "", date: today(), time: "10:00", type: "prive", pickup_address: "", dropoff_address: "" });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/patient/companies").then((r) => { setCompanies(r.data); if (r.data.length) setForm((f) => ({ ...f, company_id: r.data[0].id })); });
    api.get("/patient/favorites").then((r) => setFavorites(r.data));
  }, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setQuote(null); };

  const getQuote = async () => {
    if (!form.company_id || !form.pickup_address || !form.dropoff_address) { toast.error("Renseignez le transporteur et les adresses"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/patient/quote", { company_id: form.company_id, pickup_address: form.pickup_address, dropoff_address: form.dropoff_address });
      setQuote(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const book = async () => {
    setLoading(true);
    try {
      await api.post("/patient/book", form);
      toast.success("Réservation confirmée ! Suivez votre course dans « Mes courses ».");
      setQuote(null);
      setForm((f) => ({ ...f, pickup_address: "", dropoff_address: "" }));
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <CalendarPlus className="h-6 w-6 text-primary" /> Réserver un trajet
        </h1>
        <p className="text-muted-foreground text-sm">Taxi & tourisme auprès des transporteurs partenaires de votre région.</p>
      </div>

      <Card className="p-5 rounded-md shadow-none space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Transporteur</Label>
            <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
              <SelectTrigger className="mt-1" data-testid="reserve-company"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name} · {c.rate_per_km} €/km</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1" /></div>
          <div><Label>Heure</Label><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-1" /></div>
          <div className="col-span-2">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="mt-1" data-testid="reserve-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="prive">Trajet privé</SelectItem>
                <SelectItem value="tourisme">Circuit touristique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Adresse de départ</Label>
            <Input value={form.pickup_address} onChange={(e) => set("pickup_address", e.target.value)} className="mt-1" placeholder="Ex: Gare de Dijon Ville" data-testid="reserve-pickup" />
          </div>
          <div className="col-span-2">
            <Label>Destination</Label>
            <Input value={form.dropoff_address} onChange={(e) => set("dropoff_address", e.target.value)} className="mt-1" placeholder="Ex: Place de la Libération, Dijon" data-testid="reserve-dropoff" />
          </div>
        </div>

        {favorites.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-muted-foreground flex items-center gap-1"><Star className="h-3 w-3" /> Favoris :</span>
            {favorites.map((f) => (
              <button key={f.id} onClick={() => set("pickup_address", f.address)} className="text-xs border border-border rounded-full px-2.5 py-1 hover:bg-secondary" data-testid={`fav-chip-${f.id}`}>
                {f.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" onClick={getQuote} disabled={loading} className="gap-2" data-testid="reserve-quote"><Calculator className="h-4 w-4" /> Estimer le prix</Button>
          <Button onClick={book} disabled={loading || !quote} className="gap-2" data-testid="reserve-book"><MapPin className="h-4 w-4" /> Réserver</Button>
        </div>

        {quote && (
          <div className="bg-secondary rounded-md p-4 flex items-center justify-between" data-testid="reserve-quote-result">
            <div className="text-sm">
              <p className="text-muted-foreground">{quote.distance_km} km · ~{quote.duration_min} min</p>
              <p className="text-xs text-muted-foreground">{quote.base_fare} € + {quote.rate_per_km} €/km</p>
            </div>
            <div className="text-right">
              <p className="font-display text-3xl font-extrabold">{quote.cost} €</p>
              <Badge variant="outline" className="border-accent bg-accent/20 text-amber-700 dark:text-amber-300">Estimation</Badge>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
